/**
 * The brand and the way back, on the auth screens.
 *
 * Janeks: *"Also need an arrow to go back to landing."* Core's six auth
 * screens (`core/auth/**`) have neither — they are a card on an empty
 * background, and a visitor who clicked Play and changed their mind has no
 * route back except the browser's own button.
 *
 * ─── Why this is injected rather than added to the screens ──────────────────
 * `core/**` is template-owned and must not be edited in a game repo; a merge
 * from upstream would conflict with every line of it. So this decorates the
 * screens from OUTSIDE: `main.ts` renders whichever core screen the route
 * calls for, then mounts this into the same root. Nothing in core changes, and
 * the six screens gain the chrome together because they share one wrapper
 * class — verified, not assumed: all six are
 * `<div class="min-h-screen grid place-items-center px-5 py-10 bg-auth-gradient">`.
 *
 * The LAYOUT that makes room for this — the wrapper becoming a two-column
 * split with the settlement in the first column — lives in
 * `src/styles/app.css`, because it has to reach core's markup through the two
 * utilities a game repo owns. This module only supplies the elements that sit
 * on it.
 */
import { enhanceAuthForm } from './auth-form';
import { BRAND } from './landing-copy';
import { escapeHtml } from './landing-escape';
import { brandLogo } from './landing-chrome';

/** An arrow pointing back the way you came. Stroke-only, takes `currentColor`. */
const ICON_BACK = '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor"'
  + ' stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">'
  + '<path d="M19 12H5"/><path d="m11 6-6 6 6 6"/></svg>';

export interface AuthChromeOptions {
  /** Navigate within the SPA, so the back link does not reload the whole app. */
  navigate: (to: string) => void;
}

/**
 * Mounts the chrome into whichever auth screen is in `root`.
 *
 * A no-op — returning a teardown that does nothing — if the wrapper is not
 * there. That is not defensive padding: `main.ts` calls this for every auth
 * route, and if core ever restructures a screen the right outcome is that
 * screen losing its back link, not the route throwing.
 */
export function mountAuthChrome(root: HTMLElement, opts: AuthChromeOptions): () => void {
  const wrapper = root.querySelector<HTMLElement>('.bg-auth-gradient');
  if (!wrapper) return () => {};

  // The settlement behind this form is the same one the landing page was
  // showing, still running and now re-framed. Neither raising it nor framing
  // it happens here: `main.ts` does both from the path, BEFORE the outgoing
  // page is torn down, which is what keeps the camera travelling across the
  // navigation instead of being rebuilt on the far side of it.

  // The registration form's game-side additions (confirm field, eye toggles,
  // the terms checkbox). A no-op on the other five screens. Separate module
  // because it is form surgery rather than chrome, and it is the part most
  // likely to need changing if core's markup moves — see `auth-form.ts`.
  const formTeardown = enhanceAuthForm(root);

  const chrome = document.createElement('div');
  chrome.className = 'lp-auth-chrome';
  chrome.innerHTML = `
    <a class="lp-auth-back" href="/">
      ${ICON_BACK}<span>Back</span>
    </a>
    <div class="lp-auth-brand">
      <div class="lp-auth-brand-row">
        ${brandLogo()}
      </div>
      <p class="lp-auth-motto">${escapeHtml(BRAND.motto)}</p>
    </div>`;

  const onClick = (e: Event): void => {
    const a = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href^="/"]');
    if (!a) return;
    e.preventDefault();
    opts.navigate(a.getAttribute('href')!);
  };
  chrome.addEventListener('click', onClick);

  // Prepended, not appended: the chrome is positioned absolutely and the order
  // only matters to the tab sequence, where "go back" belongs before the form
  // rather than after the submit button.
  wrapper.prepend(chrome);

  return () => {
    formTeardown();
    chrome.removeEventListener('click', onClick);
    chrome.remove();
  };
}
